import { useState, useEffect, useCallback } from 'react';
import { Search, Sparkles, RefreshCw } from 'lucide-react';
import { paymentReconApi } from '../../services/paymentReconApi';
import { useToast } from '../ui/Toast';
import { useTableSort } from '../../hooks/useTableSort';
import { SortableTh } from './SortableTh';
import { formatMoney, formatDate, statusBadgeClass } from '../../utils/paymentReconFormat';
import { MatchPanel } from './MatchPanel';
import { CommentBadge } from './CommentBadge';
import type { CommentTarget } from './InvoiceCommentsPanel';
import type { ReconTransaction, ReconCategory } from '../../types/paymentRecon';

interface Props {
    canEdit: boolean;
    companyCode: string;
    nonCustomerCategories: ReconCategory[];
    residualReasons: ReconCategory[];
    defaultStatus?: string;
    onChanged: () => void;   // refresh stats after a mutation
    /** Open the per-invoice comments side-panel. */
    onOpenComments: (target: CommentTarget) => void;
    /** Bumped by the page after a comment change so count badges re-fetch. */
    commentsReloadKey?: number;
    /** Open this payment's match drawer as soon as the list has loaded (deep-link from an invoice). */
    openTransactionId?: number | null;
    /** Called once the deep-linked payment has been opened, so the page can clear it. */
    onOpenedTransaction?: () => void;
}

// 'matched' is intentionally omitted: it is a subset of 'resolved'
// (Matched + OnAccount + NonCustomer) and was confusingly near-identical.
const STATUS_OPTIONS = [
    { value: 'unresolved', label: 'Unresolved' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'onaccount', label: 'On account' },
    { value: 'noncustomer', label: 'Non-customer' },
    { value: 'all', label: 'All' },
];

export function TransactionsTab({ canEdit, companyCode, nonCustomerCategories, residualReasons, defaultStatus = 'unresolved', onChanged, onOpenComments, commentsReloadKey, openTransactionId, onOpenedTransaction }: Props) {
    const { toast } = useToast();
    const [transactions, setTransactions] = useState<ReconTransaction[]>([]);
    const [status, setStatus] = useState(defaultStatus);
    const [q, setQ] = useState('');
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState<ReconTransaction | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await paymentReconApi.listTransactions({ companyCode, status, q, limit: 1000 });
            setTransactions(res.transactions);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to load payments', 'error');
        } finally {
            setLoading(false);
        }
    }, [companyCode, status, q, toast]);

    useEffect(() => {
        void commentsReloadKey; // re-fetch comment-count badges after a comment change
        void load();
    }, [load, commentsReloadKey]);

    // Deep-link from an invoice: open the payment that holds its allocation. The list is
    // loaded with defaultStatus 'all' here, so the matched payment is in `transactions`.
    useEffect(() => {
        if (!openTransactionId || loading) return;
        const tx = transactions.find(t => t.Id === openTransactionId);
        if (!tx) return;
        setActive(tx);
        onOpenedTransaction?.();
    }, [openTransactionId, transactions, loading, onOpenedTransaction]);

    const { sorted, sort, toggleSort } = useTableSort(transactions, {
        date: r => r.BookingDate,
        counterparty: r => r.CounterpartyRaw,
        amount: r => r.Amount,
        status: r => r.Status,
    });

    const handleSaved = () => {
        setActive(null);
        void load();
        onChanged();
    };

    return (
        <div>
            <div className="mb-3 flex flex-wrap items-center gap-2">
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm">
                    {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') void load(); }}
                        placeholder="Search counterparty / reference…"
                        className="w-full rounded-md border border-slate-300 py-1.5 pl-8 pr-3 text-sm"
                    />
                </div>
                <button onClick={() => void load()} className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-600 hover:bg-slate-50">
                    <RefreshCw className="h-4 w-4" /> Refresh
                </button>
            </div>

            {loading ? (
                <div className="py-10 text-center text-sm text-slate-400">Loading…</div>
            ) : transactions.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">
                    No payments in this view.
                </div>
            ) : (
                <div className="max-h-[65vh] overflow-auto rounded-lg border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-slate-50">
                            <tr>
                                <SortableTh label="Date" sortKey="date" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Counterparty / Reference" sortKey="counterparty" sort={sort} onSort={toggleSort} />
                                <SortableTh label="Amount" sortKey="amount" sort={sort} onSort={toggleSort} className="text-right" />
                                <th className="px-3 py-2">Allocation</th>
                                <SortableTh label="Status" sortKey="status" sort={sort} onSort={toggleSort} />
                                {canEdit && <th className="px-3 py-2"></th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {sorted.map((t) => (
                                <tr key={t.Id} className="hover:bg-slate-50">
                                    <td className="whitespace-nowrap px-3 py-2 text-slate-500">{formatDate(t.BookingDate)}</td>
                                    <td className="px-3 py-2">
                                        <div className="font-medium text-slate-700">{t.CounterpartyRaw || '–'}</div>
                                        {t.ReferenceRaw && <div className="max-w-xs truncate text-xs text-slate-400" title={t.ReferenceRaw}>{t.ReferenceRaw}</div>}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 text-right font-medium text-slate-800">
                                        {formatMoney(t.Amount, t.Currency)}
                                        {t.RemainingAmount > 0.005 && t.MatchedAmount > 0 && (
                                            <div className="text-xs text-amber-600">open {formatMoney(t.RemainingAmount)}</div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-500">
                                        {t.allocations.length > 0
                                            ? t.allocations.map(a => (
                                                <div key={a.Id} className="flex items-center gap-1.5 truncate">
                                                    <button
                                                        type="button"
                                                        onClick={() => onOpenComments({ invoiceId: a.InvoiceId, invoiceNo: a.InvoiceNo, customerName: a.CustomerName })}
                                                        className="truncate text-left text-slate-600 hover:text-amber-700 hover:underline"
                                                        title="Open comments"
                                                    >
                                                        {a.InvoiceNo} · {formatMoney(a.Amount)}
                                                    </button>
                                                    <CommentBadge count={a.CommentCount ?? 0} onClick={() => onOpenComments({ invoiceId: a.InvoiceId, invoiceNo: a.InvoiceNo, customerName: a.CustomerName })} />
                                                </div>
                                            ))
                                            : t.NonCustomerCategoryLabel
                                                ? <span className="text-violet-600">{t.NonCustomerCategoryLabel}</span>
                                                : <span className="text-slate-300">—</span>}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(t.Status)}`}>
                                            {t.AutoMatched && <Sparkles className="h-3 w-3" />}
                                            {t.Status}
                                        </span>
                                    </td>
                                    {canEdit && (
                                        <td className="px-3 py-2 text-right">
                                            <button
                                                onClick={() => setActive(t)}
                                                className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-amber-50 hover:text-amber-700"
                                            >
                                                {t.Status === 'Unmatched' ? 'Match' : 'Edit'}
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {active && canEdit && (
                <MatchPanel
                    transaction={active}
                    nonCustomerCategories={nonCustomerCategories}
                    residualReasons={residualReasons}
                    onClose={() => setActive(null)}
                    onSaved={handleSaved}
                    onOpenComments={onOpenComments}
                />
            )}
        </div>
    );
}
